import { compact, keyBy } from "lodash";

import { Customer } from "../Customer/schema";
import { CustomerTab, OrderItem, Transaction } from "../CustomerTab/schema";
import { sumTransactions } from "../CustomerTab/selectors";
import { BillSplit } from "./schema";

const getOrderItemClaimEntities = (billSplit: BillSplit) =>
  keyBy(billSplit.orderItemClaims, "orderItemId");

const getAllOrderItems = (customerTab: CustomerTab) =>
  customerTab.orders.flatMap((o) => o.orderItems);
const getOrderItems = (customerTab: CustomerTab) =>
  customerTab.orders.flatMap((o) => o.orderItems.filter((oi) => !oi.voided));

const withoutRefunds = (customerTab: CustomerTab) => {
  const refundUuids = compact(
    customerTab.orders.flatMap((o) => o.orderItems.map((oi) => oi.refundUuid)),
  );
  return (transaction: Transaction) => !refundUuids.includes(transaction.uuid);
};

export const hasCustomerPaidIn =
  (billSplit: BillSplit) => (customer: Customer) => {
    const customerSession = billSplit.customerSessions.find(
      (cs) => cs.customerId === customer.uuid,
    );
    return !!customerSession?.completedAt;
  };
export const getOrderItemCountIn =
  (billSplit: BillSplit, customerTab: CustomerTab) => (customer: Customer) => {
    const claimedOrderItems = getClaimedOrderItemsIn(
      billSplit,
      customerTab,
    )(customer);
    return claimedOrderItems.length;
  };

const getClaimedOrderItems = (
  billSplit: BillSplit,
  customerTab: CustomerTab,
) => {
  const orderItems = getOrderItems(customerTab);
  const orderItemClaims = getOrderItemClaimEntities(billSplit);
  const claimedOrderItems =
    orderItems.filter((oi) => orderItemClaims[oi.uuid]?.customerId) || [];
  return claimedOrderItems;
};

export const getOthersClaimedValue = (
  billSplit: BillSplit,
  customerTab: CustomerTab,
  customer: Customer,
) => {
  const orderItems = getOrderItems(customerTab);
  const orderItemClaims = getOrderItemClaimEntities(billSplit);
  const claimedOrderItems = orderItems.filter(
    (oi) =>
      orderItemClaims[oi.uuid] &&
      orderItemClaims[oi.uuid]?.customerId !== customer.uuid,
  );
  return getOrderItemsTotal(claimedOrderItems);
};
export const getOthersClaimedUnpaidOrderItems = (
  billSplit: BillSplit,
  customerTab: CustomerTab,
  customer: Customer,
) => {
  const orderItems = getOrderItems(customerTab);
  const orderItemClaims = getOrderItemClaimEntities(billSplit);
  const claimedOrderItems = orderItems.filter(
    (oi) =>
      orderItemClaims[oi.uuid] &&
      orderItemClaims[oi.uuid]?.customerId !== customer.uuid &&
      !oi.paymentUuid,
  );
  return getOrderItemsTotal(claimedOrderItems);
};
export const getClaimedOrderItemsIn =
  (billSplit: BillSplit, customerTab: CustomerTab) => (customer: Customer) => {
    const orderItems = getOrderItems(customerTab);
    const orderItemClaims = getOrderItemClaimEntities(billSplit);
    const claimedOrderItems =
      orderItems.filter(
        (oi) => orderItemClaims[oi.uuid]?.customerId === customer.uuid,
      ) || [];
    return claimedOrderItems;
  };

export const getUnclaimedOrderItems = (
  billSplit: BillSplit,
  customerTab: CustomerTab,
) => {
  const orderItemClaims = getOrderItemClaimEntities(billSplit);
  const orderItems = getOrderItems(customerTab);
  const unclaimedOrderItems =
    orderItems?.filter((oi) => !orderItemClaims[oi.uuid] && !oi.paymentUuid) ||
    [];
  return unclaimedOrderItems;
};

export const getOrderItemsTotal = (orderItems: OrderItem[]) => {
  return orderItems
    .map((oi) => oi.totalSalesPriceTaxInclusive)
    .reduce((a, b) => a + b, 0);
};

export const getTotalRemainingUnpaid = (customerTab: CustomerTab) => {
  const totalPaid = sumTransactions(
    customerTab.transactions.filter(withoutRefunds(customerTab)),
  );
  const totalBill = getOrderItemsTotal(getOrderItems(customerTab));
  return totalBill - totalPaid;
};

export const getTotalRemainingUnclaimed = (
  billSplit: BillSplit,
  customerTab: CustomerTab,
) => {
  const claimedUnpaidOrderItems = getClaimedOrderItems(
    billSplit,
    customerTab,
  ).filter((oi) => !oi.paymentUuid);

  const totalClaimed = getOrderItemsTotal(claimedUnpaidOrderItems);
  const totalPaid = sumTransactions(
    customerTab.transactions.filter(withoutRefunds(customerTab)),
  );
  const totalBill = getOrderItemsTotal(getOrderItems(customerTab));
  return totalBill - (totalClaimed + totalPaid);
};

export const getPaidAtTillOrderItems = (
  billSplit: BillSplit,
  customerTab: CustomerTab,
) => {
  const onlinePayments = billSplit.customerSessions
    .filter((cs) => cs.paymentUuid)
    .map((cs) => cs.paymentUuid);

  const paidAtTillOrderItems = getOrderItems(customerTab)
    .filter((oi) => oi.paymentUuid)
    .filter((oi) => !onlinePayments.includes(oi.paymentUuid));
  return paidAtTillOrderItems;
};

export const getPaidAtTillAmount = (
  billSplit: BillSplit,
  customerTab: CustomerTab,
) => {
  const onlinePayments = billSplit.customerSessions
    .filter((cs) => cs.paymentUuid)
    .map((cs) => cs.paymentUuid);

  const paidAtTillAmount = sumTransactions(
    customerTab.transactions
      .filter((t) => !onlinePayments.includes(t.uuid))
      .filter(withoutRefunds(customerTab)),
  );
  return paidAtTillAmount;
};

export const getTotalUnassignedTransactionsFor = (
  billSplit: BillSplit,
  customerTab: CustomerTab,
) => {
  const orderItems = getOrderItems(customerTab);
  const orderItemTransactionsUuids = orderItems
    .filter((oi) => oi.paymentUuid)
    .map((oi) => oi.paymentUuid);
  const transactions = customerTab.transactions
    .filter((t) => !orderItemTransactionsUuids.includes(t.uuid))
    .filter(withoutRefunds(customerTab));
  return sumTransactions(transactions);
};

export const getBillSplitPaidOrderItems = (
  billSplit: BillSplit,
  customerTab: CustomerTab,
) => {
  const orderItemClaims = getOrderItemClaimEntities(billSplit);
  const completedSessionIds = billSplit.customerSessions
    .filter((cs) => !!cs.completedAt)
    .map((cs) => cs.uuid);
  const paidOrderItems = getOrderItems(customerTab).filter((oi) =>
    completedSessionIds.includes(orderItemClaims[oi.uuid]?.customerSessionId),
  );
  return paidOrderItems;
};

const isOwner = (billSplit: BillSplit, customer: Customer) => {
  return billSplit.ownerId === customer.uuid;
};

export const getTotalPaid = (customerTab: CustomerTab) => {
  return sumTransactions(
    customerTab.transactions.filter(withoutRefunds(customerTab)),
  );
};
export const getCustomerTabBalance = (customerTab: CustomerTab) => {
  const totalOrderItems = getOrderItemsTotal(getOrderItems(customerTab));
  const totalTransactions = sumTransactions(
    customerTab.transactions.filter(withoutRefunds(customerTab)),
  );

  return totalOrderItems - totalTransactions;
};
